import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  HostListener,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { PdfViewerComponent, PageRenderedEvent } from '../project-annotator/pdf-viewer.component';
import { ClaimPickerPopoverComponent } from '../project-annotator/claim-picker.popover.component';
import { NoteEditorComponent } from '../project-annotator/note-editor.component';
import { OcrEditorComponent } from '../project-annotator/ocr-editor.component';
import { TextInputOverlayComponent } from '../project-annotator/text-input-overlay.component';
import {
  AnnotationOverlayDirective,
  OverlayDrawCompleted,
  OverlayTextPointPicked,
} from '../project-annotator/annotation-overlay.directive';

import { DataExtractionQuestionnaireComponent } from '../data-extraction-questionnaire/data-extraction-questionnaire.component';
import { DataExtractionAgreementCardComponent } from '../data-extraction-agreement-card/data-extraction-agreement-card.component';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';
import { CLAIMS_PROVIDER } from '../../../claim-annotator/ports/claims-provider.port';
import { ANNOTATION_REPOSITORY } from '../../../claim-annotator/ports/annotation-repository.port';
import { ANNOTATION_DRAFT_CACHE } from '../../../claim-annotator/ports/annotation-draft-cache.port';
import { USER_CONTEXT } from '../../../claim-annotator/ports/user-context.port';

import { InMemoryPdfSource } from '../../../claim-annotator/adapters/in-memory-pdf-source';
import { HttpAnnotationsAdapter } from '../../../claim-annotator/adapters/http-annotations';
import { LocalStorageDraftCache } from '../../../claim-annotator/adapters/local-storage-draft-cache';
import { AuthUserContext } from '../../../claim-annotator/adapters/auth-user-context';
import { QuestionnaireClaimsAdapter } from '../../../claim-annotator/adapters/questionnaire-claims.adapter';
import { QUESTIONNAIRE_PORT } from '../../../extraction-schema/ports/questionnaire.port';
import { HttpQuestionnaireAdapter } from '../../../extraction-schema/adapters/http-questionnaire.adapter';

import { AnnotationStore } from '../../../claim-annotator/domain/annotation-store';
import { AnnotationWorkflow } from '../../../claim-annotator/domain/annotation-workflow';
import { ClaimSetState } from '../../../claim-annotator/domain/claim-set-state';
import { AnnotationRow, PageGeometry, Claim } from '../../../claim-annotator/domain/types';

import { HttpPdfCorpus } from '../../../pdf-corpus/adapters/http-pdf-corpus';
import { PDF_CORPUS_PORT } from '../../../pdf-corpus/ports/pdf-corpus.port';
import { PdfCorpusService } from '../../../pdf-corpus/domain/pdf-corpus.service';

import { InMemoryProjectCorpus } from '../../../project-corpus/adapters/in-memory-project-corpus';
import { HybridProjectCorpus } from '../../../project-corpus/adapters/hybrid-project-corpus';
import { PROJECT_CORPUS_PORT } from '../../../project-corpus/ports/project-corpus.port';

import { ANSWER_REPOSITORY } from '../../../extraction-answers/ports/answer-repository.port';
import { ANSWER_DRAFT_CACHE } from '../../../extraction-answers/ports/answer-draft-cache.port';
import { LocalStorageAnswerCache } from '../../../extraction-answers/adapters/local-storage-answer-cache';
import { HttpAnswersAdapter } from '../../../extraction-answers/adapters/http-answers';
import { AnswerStore } from '../../../extraction-answers/domain/answer-store';

import { DataExtractionService } from '../../../services/data-extraction.service';
import { ProjectService } from '../../../services/project.service';
import {
  DataExtractionConflictService,
  CompletionStatus,
} from '../../../services/data-extraction-conflict.service';
import { ToastService } from '../../../services/toast.service';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { DataExtractionPaper, DataExtractionStats } from '../../../dtos/data-extraction';
import { Project } from '../../../dtos/project';
import { ProjectState } from '../../../dtos/project-state';
import { ExtractionQuestion } from '../../../extraction-schema/domain/extraction-question.model';
import { AnswerRow, SKIPPED_VALUE } from '../../../extraction-answers/domain/types';

@Component({
  selector: 'app-project-data-extraction',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    PdfViewerComponent,
    ClaimPickerPopoverComponent,
    NoteEditorComponent,
    OcrEditorComponent,
    RouterLink,
    TextInputOverlayComponent,
    AnnotationOverlayDirective,
    DataExtractionQuestionnaireComponent,
    DataExtractionAgreementCardComponent,
    BaseModalComponent,
  ],
  providers: [
    InMemoryPdfSource,
    QuestionnaireClaimsAdapter,
    HttpAnnotationsAdapter,
    LocalStorageDraftCache,
    AuthUserContext,
    { provide: PDF_SOURCE, useExisting: InMemoryPdfSource },
    { provide: QUESTIONNAIRE_PORT, useClass: HttpQuestionnaireAdapter },
    { provide: CLAIMS_PROVIDER, useExisting: QuestionnaireClaimsAdapter },
    { provide: ANNOTATION_REPOSITORY, useClass: HttpAnnotationsAdapter },
    { provide: ANNOTATION_DRAFT_CACHE, useClass: LocalStorageDraftCache },
    { provide: USER_CONTEXT, useExisting: AuthUserContext },
    ClaimSetState,
    AnnotationStore,
    AnnotationWorkflow,
    HttpPdfCorpus,
    { provide: PDF_CORPUS_PORT, useExisting: HttpPdfCorpus },
    PdfCorpusService,
    InMemoryProjectCorpus,
    HybridProjectCorpus,
    { provide: PROJECT_CORPUS_PORT, useExisting: HybridProjectCorpus },
    LocalStorageAnswerCache,
    HttpAnswersAdapter,
    { provide: ANSWER_DRAFT_CACHE, useClass: LocalStorageAnswerCache },
    { provide: ANSWER_REPOSITORY, useClass: HttpAnswersAdapter },
    AnswerStore,
  ],
  templateUrl: './project-data-extraction.component.html',
})
export class ProjectDataExtractionComponent implements OnInit, HasUnsavedChanges {
  readonly ProjectState = ProjectState;
  readonly annotationStore = inject(AnnotationStore);
  readonly answerStore = inject(AnswerStore);
  readonly workflow = inject(AnnotationWorkflow);
  protected claims = inject(ClaimSetState);
  private pdfSource = inject(InMemoryPdfSource);
  private claimsProvider = inject(CLAIMS_PROVIDER);
  private pdfCorpus = inject(PdfCorpusService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);
  private router = inject(Router);
  private dataExtractionService = inject(DataExtractionService);
  private projectService = inject(ProjectService);
  private conflictService = inject(DataExtractionConflictService);
  private questionnairePort = inject(QUESTIONNAIRE_PORT);
  private projectCorpusPort = inject(PROJECT_CORPUS_PORT);
  private location = inject(Location);

  readonly projectId = signal<string>('');
  readonly project = signal<Project | null>(null);
  /** True when the current user is the project's PI (gates the Resolve Conflicts entry point). */
  readonly isPI = signal<boolean>(false);
  readonly currentPaper = signal<DataExtractionPaper | null>(null);
  readonly stats = signal<DataExtractionStats | null>(null);
  readonly completedPapers = signal<DataExtractionPaper[]>([]);
  /** True while viewing/editing a paper that is already COMPLETED (no lock, no Complete button). */
  readonly editingCompleted = signal(false);
  readonly questions = signal<ExtractionQuestion[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly completing = signal(false);
  readonly skipping = signal(false);
  readonly lastError = signal<string | null>(null);
  readonly showCompletedList = signal(false);
  readonly showTeamModal = signal(false);
  readonly hasRequestedFirst = signal(false);
  /** Case-insensitive search query for the completed-papers list (title, authors, year, DOI). */
  readonly completedQuery = signal('');

  /** PI completion gate (unresolved conflicts / second-reviewer needs); null until/if it loads. */
  readonly conflictStatus = signal<CompletionStatus | null>(null);

  /** The fetched project's lifecycle state and completion timestamp (drives the completed banner). */
  readonly projectState = signal<string | null>(null);
  readonly completedAt = signal<string | null>(null);

  /** True once the project has advanced past data extraction (state COMPLETED). */
  readonly isCompleted = computed(() => this.projectState() === 'COMPLETED');

  /** True when the project is COMPLETED, meaning extraction data is read-only (Audit Mode). */
  readonly isAuditMode = computed(() => this.project()?.state === 'COMPLETED');

  readonly pageGeometries = signal<Map<number, PageGeometry>>(new Map());
  readonly pageElements = signal<Map<number, HTMLElement>>(new Map());
  readonly drawMode = signal<'box' | 'text' | null>(null);

  /** Ratio of questions-panel width to total two-column width (0.2–0.8). */
  readonly splitRatio = signal(0.5);
  readonly dividerDrag = signal<{
    startX: number;
    startRatio: number;
    containerWidth: number;
  } | null>(null);

  /** True while the near-fullscreen extraction workspace modal is open (the questions+PDF editor). */
  readonly workspaceOpen = signal(false);
  /** Read-only workspace (e.g. a completed/locked paper): no Save/Draw, overlay rendered read-only. */
  readonly workspaceReadOnly = signal(false);
  /** The client coordinates of the last completed draw, used to anchor the claim popover near it. */
  readonly lastDrawClient = signal<{ x: number; y: number } | null>(null);

  /**
   * The client coordinates of the last pointerdown anywhere on the page. The note editor anchors to
   * this (a note has no draw coordinates of its own): the pointerdown that clicks the note button
   * sets it to that button's location, so the editor opens next to the note button.
   */
  readonly lastPointer = signal<{ x: number; y: number } | null>(null);

  /** Template reference to the PDF viewer, used to scroll to a page when an OCR text line is clicked. */
  @ViewChild('pdfViewer') pdfViewer!: PdfViewerComponent;

  /**
   * Signal holding the annotation id and a sequence counter for the overlay-highlight pulse.
   * Incrementing the sequence forces the overlay's render effect to re-run even when jumping
   * to the same box twice.
   */
  readonly highlightedAnnotation = signal<{ id: string; seq: number } | null>(null);
  private highlightSeq = 0;

  /**
   * True while any inner editor overlay (claim picker, text input, note editor) is open. While
   * active the workspace modal must not close on Escape so Escape dismisses the overlay instead.
   */
  readonly innerOverlayActive = computed(
    () =>
      this.workflow.pendingDraft() !== null ||
      this.workflow.pendingTextPoint() !== null ||
      this.workflow.editingNoteForId() !== null,
  );

  /** Backdrop clicks must not close the modal while drawing or while an inner overlay is open. */
  readonly backdropLocked = computed(() => this.innerOverlayActive() || this.drawMode() !== null);

  private loadToken = 0;
  private readonly resolvedPdfIds = new Map<string, number>();
  /**
   * Document key (`paper:{paperId}`) whose PDF/claims/annotations have been loaded. Lets
   * openWorkspace load the resumed paper's data on demand (after a reload, resumeCurrentOrWait
   * restores only the paper metadata) while skipping a reload when it is already loaded.
   */
  private loadedDocKey: string | null = null;
  /**
   * PaperToProjectId whose answers/annotations were hydrated during resumeCurrentOrWait.
   * Prevents redundant hydrateFromRepository when loadPaperData runs later.
   */
  private hydratedPtpKey: string | null = null;

  /** The document key for the current paper (paper:<paperId>). */
  private get annoKey(): string {
    const p = this.currentPaper();
    return p ? `paper:${p.paperId}` : '';
  }

  /** The answer cache key for the current paper (String(paperToProjectId)). */
  private get ptpKey(): string {
    const p = this.currentPaper();
    return p ? String(p.paperToProjectId) : '';
  }

  /** Computed set of questionIds that have at least one annotation box bound to them. */
  readonly annotatedQuestionIds = computed<Set<number>>(() => {
    const docKey = this.annoKey;
    if (!docKey) return new Set();
    const annotations = this.annotationStore.annotations().filter((a) => a.document_id === docKey);
    const ids = new Set<number>();
    for (const a of annotations) {
      if (a.claim_id.startsWith('q:')) {
        const id = Number(a.claim_id.slice(2));
        if (!isNaN(id)) ids.add(id);
      }
    }
    return ids;
  });

  /** Unified unsaved changes signal: either store has unsaved work. */
  readonly hasUnifiedUnsavedChanges = computed<boolean>(
    () => this.answerStore.hasUnsavedChanges() || this.annotationStore.hasAnyUnsavedChanges(),
  );

  /**
   * The completed papers filtered by the case-insensitive search query over title, authors,
   * the publication year (derived from publicationDate) and DOI. Empty query shows all.
   */
  readonly filteredCompleted = computed<DataExtractionPaper[]>(() => {
    const q = this.completedQuery().trim().toLowerCase();
    const all = this.completedPapers();
    if (!q) return all;
    return all.filter((p) => {
      const year = p.publicationDate ? p.publicationDate.slice(0, 4) : '';
      const haystack = [p.title ?? '', p.authors ?? '', year, p.doi ?? ''].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  });

  /** Annotations for the active document (filtered to the current paper's doc). */
  readonly currentAnnotations = computed<AnnotationRow[]>(() => {
    const docKey = this.annoKey;
    if (!docKey) return [];
    return this.annotationStore.annotations().filter((a) => a.document_id === docKey);
  });

  /** Current answers for the active paper. */
  readonly currentAnswers = computed<AnswerRow[]>(() => {
    const key = this.ptpKey;
    if (!key) return [];
    return this.answerStore.answersFor(key)();
  });

  /**
   * Client-side gate: all required questions must be answered AND each annotated.
   * Server re-checks on Complete.
   */
  readonly canComplete = computed<boolean>(() => {
    const paper = this.currentPaper();
    if (!paper) return false;
    const required = this.questions().filter((q) => q.required);
    if (required.length === 0) return true;
    const annotated = this.annotatedQuestionIds();
    const answers = this.currentAnswers();
    return required.every((q) => {
      const row = answers.find((r) => r.questionId === q.id);
      if (!row || !isAnswered(q, row.value)) return false;
      // A skipped question states the paper has no answer; no evidence box required.
      return row.value === SKIPPED_VALUE || annotated.has(q.id!);
    });
  });

  /** Blocked reason text when canComplete is false (A13). */
  readonly blockedReason = computed<string>(() => {
    if (this.canComplete()) return '';
    const required = this.questions().filter((q) => q.required);
    const annotated = this.annotatedQuestionIds();
    const answers = this.currentAnswers();
    let unanswered = 0;
    let unannotated = 0;
    for (const q of required) {
      const row = answers.find((r) => r.questionId === q.id);
      const answered = row && isAnswered(q, row.value);
      if (!answered) {
        unanswered++;
        continue;
      }
      if (row.value !== SKIPPED_VALUE && !annotated.has(q.id!)) unannotated++;
    }
    const parts: string[] = [];
    if (unanswered > 0)
      parts.push(`${unanswered} required question${unanswered > 1 ? 's' : ''} unanswered`);
    if (unannotated > 0) parts.push(`${unannotated} without an evidence box`);
    return parts.join(', ');
  });

  /** Per-question completion status for the checklist card between toolbar and workspace launcher. */
  readonly questionStatuses = computed(() => {
    const questions = this.questions();
    const answers = this.currentAnswers();
    const annotated = this.annotatedQuestionIds();
    return questions.map((q) => {
      const answer = answers.find((r) => r.questionId === q.id);
      const value = answer?.value;
      const answered =
        !!value && value.trim() !== '' && !(q.type === 'MULTI_CHOICE' && value.trim() === '[]');
      const skipped = value === SKIPPED_VALUE;
      const hasAnnotation = q.id != null && annotated.has(q.id);
      return {
        question: q,
        answered,
        skipped,
        hasAnnotation,
        complete: answered && (hasAnnotation || skipped || !q.required),
        needsEvidence: answered && !hasAnnotation && !skipped && q.required,
      };
    });
  });

  /** HasUnsavedChanges guard interface: returns true when it is safe to leave. */
  hasUnsavedChanges(): boolean {
    return this.answerStore.hasUnsavedChanges() || this.annotationStore.hasAnyUnsavedChanges();
  }

  /** Template helper for reading unified unsaved state (avoids calling method in template). */
  get unifiedHasUnsaved(): boolean {
    return this.hasUnsavedChanges();
  }

  /** Template helper: true when the Complete button should be disabled and shown as greyed. */
  get isCompleteDisabled(): boolean {
    return (
      !this.canComplete() || this.hasUnifiedUnsavedChanges() || this.saving() || this.completing()
    );
  }

  /** Toggle the visibility of the completed papers list. */
  toggleCompletedList(): void {
    this.showCompletedList.update((v) => !v);
  }

  /**
   * Save is blocked while editing an already-completed paper whose current edit would make it
   * invalid (a required question unanswered or missing its evidence box). A completed paper must
   * stay valid (decision 8); saving would otherwise persist the annotation changes independently
   * of the rejected answer-save and corrupt it. Drafts can always be saved as partial work.
   * Additionally, saving is entirely blocked if the project itself is COMPLETED (read-only audit).
   */
  get saveDisabled(): boolean {
    if (this.project()?.state === 'COMPLETED') return true;
    return this.saving() || (this.editingCompleted() && !this.canComplete());
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    // Freeze the note anchor while an inner overlay is open: a pointerdown inside the open note
    // editor (e.g. clicking its textarea) must not re-anchor and move the editor to the click point.
    if (this.innerOverlayActive()) return;
    // Divider drags capture the pointer — don't clobber lastPointer while dragging.
    if (this.dividerDrag()) return;
    this.lastPointer.set({ x: event.clientX, y: event.clientY });
  }

  onDividerDragStart(event: PointerEvent, container: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) return;
    this.dividerDrag.set({
      startX: event.clientX,
      startRatio: this.splitRatio(),
      containerWidth,
    });
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  onDividerDragMove(event: PointerEvent): void {
    const drag = this.dividerDrag();
    if (!drag || event.buttons !== 1) return;
    const dx = event.clientX - drag.startX;
    const newRatio = drag.startRatio + dx / drag.containerWidth;
    if (!isFinite(newRatio)) return;
    this.splitRatio.set(Math.max(0.2, Math.min(0.8, newRatio)));
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  @HostListener('document:lostpointercapture')
  onDividerDragEnd(): void {
    if (!this.dividerDrag()) return;
    this.dividerDrag.set(null);
  }

  /**
   * Escape, while the workspace is open, dismisses an open inner overlay (note editor, claim picker,
   * or text input) and must NOT close the workspace. This runs at the capture phase so it reliably
   * beats the base modal's document keydown.escape handler regardless of where focus is (the
   * per-input Escape handlers only fire while their own field is focused, and the modal's
   * closeOnEscape binding updates a change-detection tick later). With no inner overlay open, Escape
   * is left to propagate so the modal closes the workspace as usual.
   */
  private handleEscapeCapture(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.workspaceOpen()) return;
    if (this.workflow.editingNoteForId() !== null) {
      this.onNoteEditorCancelled();
    } else if (this.workflow.pendingDraft() !== null) {
      this.onClaimPickerCancelled();
    } else if (this.workflow.pendingTextPoint() !== null) {
      this.onTextInputCancelled();
    } else {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    if (!isCtrlOrCmd || event.key !== 's') return;
    event.preventDefault();
    const editorOpen =
      this.workflow.pendingTextPoint() !== null ||
      this.workflow.pendingDraft() !== null ||
      this.workflow.editingNoteForId() !== null;
    if (editorOpen) return;
    if (this.hasUnsavedChanges() && !this.saving()) {
      void this.onSaveProgress();
    }
  }

  ngOnInit(): void {
    // Capture-phase Escape handler so an open inner overlay wins over the base modal's close.
    const onEscapeCapture = (e: KeyboardEvent): void => this.handleEscapeCapture(e);
    document.addEventListener('keydown', onEscapeCapture, true);
    this.destroyRef.onDestroy(() => document.removeEventListener('keydown', onEscapeCapture, true));

    const id = this.route.parent?.snapshot.paramMap.get('projectId') ?? '';
    this.projectId.set(id);
    if (!id) return;
    this.loadProjectRole(id);
    this.loadStats();
    this.loadCompleted();

    const reopenId = this.route.snapshot.queryParamMap?.get('reopen');
    if (reopenId) {
      this.dataExtractionService.getPaper(id, Number(reopenId)).subscribe({
        next: (paper: DataExtractionPaper) => void this.onReopenCompleted(paper),
        error: () => void this.loadNextPaper(),
      });
    } else {
      this.resumeCurrentOrWait();
    }

    // Also load the questionnaire questions for the page
    this.loadQuestions(id);
  }

  /**
   * Load the project to determine the current user's role and the project's lifecycle state.
   * The PI also gets the completion gate (unresolved conflicts) so the Conflict Resolution button
   * can surface a count badge.
   */
  private loadProjectRole(projectId: string): void {
    this.projectService.getProjectById(projectId).subscribe({
      next: (project) => {
        this.project.set(project);
        const isPi = project?.userRole === 'PI';
        this.isPI.set(isPi);
        this.projectState.set(project?.state ?? null);
        this.completedAt.set(project?.completedAt ?? null);
        if (isPi) this.loadConflictStatus(projectId);
      },
      error: () => {
        this.project.set(null);
        this.isPI.set(false);
        this.projectState.set(null);
        this.completedAt.set(null);
      },
    });
  }

  /** Fetch the PI completion gate; leave it null on error so the button stays plain (no badge). */
  private loadConflictStatus(projectId: string): void {
    this.conflictService.completionStatus(Number(projectId)).subscribe({
      next: (s) => this.conflictStatus.set(s),
      error: () => this.conflictStatus.set(null),
    });
  }

  /** PI entry point into the data-extraction conflict-resolution overview. */
  navigateToConflicts(): void {
    void this.router.navigate(['/projects', this.projectId(), 'data-extraction', 'conflicts']);
  }

  private loadQuestions(projectId: string): void {
    this.questionnairePort.getQuestions(projectId).subscribe({
      next: (qs) => this.questions.set(qs),
      error: (err) => {
        console.error('Failed to load extraction questions:', err);
        this.questions.set([]);
      },
    });
  }

  loadStats(): void {
    const id = this.projectId();
    if (!id) return;
    this.dataExtractionService.getStats(id).subscribe({
      next: (s) => {
        this.stats.set(s);
        if (s.unextractedPapers === 0) {
          this.hasRequestedFirst.set(true);
        }
      },
      error: (e: unknown) => {
        if (e instanceof HttpErrorResponse && e.status === 401) {
          void this.router.navigate(['/login']);
        }
      },
    });
  }

  loadCompleted(): void {
    const id = this.projectId();
    if (!id) return;
    this.dataExtractionService.getCompleted(id).subscribe({
      next: (list) => this.completedPapers.set(list),
      error: () => {},
    });
  }

  /**
   * Discard unsaved edits to the paper currently open before navigating away from it. Only used
   * when leaving an editingCompleted paper: a completed paper's abandoned edits must not linger in
   * the stores, or the next (clean) queue paper would still report unsaved changes. A draft queue
   * paper's in-progress work is intentionally kept (it is resumable via the held lock).
   */
  private discardCurrentPaperEdits(): void {
    const paper = this.currentPaper();
    if (!paper) return;
    this.answerStore.discardChanges(String(paper.paperToProjectId));
    this.annotationStore.discardChanges(`paper:${paper.paperId}`);
  }

  /** Reopen a completed paper for editing without calling /next or locking. Stays COMPLETED. */
  async onReopenCompleted(paper: DataExtractionPaper): Promise<void> {
    if (this.editingCompleted()) this.discardCurrentPaperEdits();
    const token = ++this.loadToken;
    this.loading.set(true);
    this.lastError.set(null);
    this.editingCompleted.set(true);
    // A completed project's papers can only be viewed, never edited; open the workspace read-only.
    this.workspaceReadOnly.set(this.isCompleted());
    this.workspaceOpen.set(true);
    try {
      this.currentPaper.set(paper);
      await this.loadPaperData(paper, token);
    } catch (e: unknown) {
      if (token !== this.loadToken) return;
      if (e instanceof HttpErrorResponse && e.status === 401) {
        void this.router.navigate(['/login']);
        return;
      }
      this.lastError.set(this.asyncErrorMessage(e));
    } finally {
      if (token === this.loadToken) this.loading.set(false);
    }
  }

  private resumeCurrentOrWait(): void {
    this.dataExtractionService.getCurrentPaper(this.projectId()).subscribe({
      next: (paper: DataExtractionPaper | null) => {
        if (paper) {
          this.currentPaper.set(paper);
          this.hasRequestedFirst.set(true);
          // Load saved answers and annotations so the status checklist shows
          // progress immediately, without waiting for the workspace to open.
          const ptpKey = String(paper.paperToProjectId);
          this.hydratedPtpKey = ptpKey;
          void this.answerStore.hydrateFromRepository(ptpKey);
          void this.annotationStore.hydrateFromRepository(`paper:${paper.paperId}`);
        }
      },
      error: () => {
        // Ignore — show the start button
      },
    });
  }

  async loadNextPaper(): Promise<void> {
    if (this.editingCompleted()) this.discardCurrentPaperEdits();
    this.hasRequestedFirst.set(true);
    const token = ++this.loadToken;
    const id = this.projectId();
    if (!id) return;
    this.loading.set(true);
    this.lastError.set(null);
    this.editingCompleted.set(false);
    this.workspaceReadOnly.set(false);
    try {
      const paper = await firstValueFrom(this.dataExtractionService.getNextPaper(id));
      if (token !== this.loadToken) return;
      this.currentPaper.set(paper);
      if (paper) {
        await this.loadPaperData(paper, token);
      }
    } catch (e: unknown) {
      if (token !== this.loadToken) return;
      this.currentPaper.set(null);
      if (e instanceof HttpErrorResponse && e.status === 401) {
        void this.router.navigate(['/login']);
        return;
      }
      // When the project has already advanced past data extraction the backend rejects /next with
      // a phase error (HTTP 409, "...not in Data Extraction phase..."). That is expected stale
      // client state, not a real failure: the completed banner is shown instead of a red error.
      // The phase error shares its 409 status with legitimate validation errors, so it cannot be
      // told apart by status alone; we suppress it whenever the project is known to be completed.
      if (this.isCompleted()) return;
      this.lastError.set(this.asyncErrorMessage(e));
    } finally {
      if (token === this.loadToken) this.loading.set(false);
    }
  }

  private async loadPaperData(paper: DataExtractionPaper, token: number): Promise<void> {
    const docKey = `paper:${paper.paperId}`;
    const ptpKey = String(paper.paperToProjectId);
    this.loadedDocKey = docKey;

    // Load the PDF
    try {
      if (paper.pdfId != null) {
        const summary = await firstValueFrom(
          this.projectCorpusPort.getProjectSummary(this.projectId()),
        );
        if (token !== this.loadToken) return;
        const blob = await firstValueFrom(
          this.pdfCorpus.fetchBytes(Number(summary.groupId), paper.pdfId),
        );
        if (token !== this.loadToken) return;
        this.openPaperDocument(docKey, 'file', paper.title || docKey);
        this.pdfSource.setBlob(blob, paper.title || `${paper.paperId}.pdf`);
      } else if (paper.pdfUrl != null) {
        const summary = await firstValueFrom(
          this.projectCorpusPort.getProjectSummary(this.projectId()),
        );
        if (token !== this.loadToken) return;
        let pdfId: number;
        if (this.resolvedPdfIds.has(String(paper.paperId))) {
          pdfId = this.resolvedPdfIds.get(String(paper.paperId))!;
        } else {
          pdfId = await firstValueFrom(
            this.pdfCorpus.materializePdf(Number(summary.groupId), paper.paperId),
          );
          this.resolvedPdfIds.set(String(paper.paperId), pdfId);
        }
        if (token !== this.loadToken) return;
        const blob = await firstValueFrom(
          this.pdfCorpus.fetchBytes(Number(summary.groupId), pdfId),
        );
        if (token !== this.loadToken) return;
        this.openPaperDocument(docKey, 'file', paper.title || docKey);
        this.pdfSource.setBlob(blob, paper.title || `${paper.paperId}.pdf`);
      }
    } catch (e) {
      // PDF loading failure is non-fatal (the questionnaire still shows), but surface the
      // reason instead of swallowing it so a genuine load failure is diagnosable.
      this.toast.error('Could not load the PDF', this.asyncErrorMessage(e));
    }

    if (token !== this.loadToken) return;

    // Load claims (questions) for the annotator
    await this.loadClaimsForDocument(docKey);

    // Hydrate both stores (skip if already hydrated during resumeCurrentOrWait)
    if (this.hydratedPtpKey === ptpKey) {
      this.hydratedPtpKey = null;
    } else {
      const [,] = await Promise.allSettled([
        this.annotationStore.hydrateFromRepository(docKey),
        this.answerStore.hydrateFromRepository(ptpKey),
      ]);
    }

    if (token !== this.loadToken) return;

    // Unified restore toast (A15)
    const annoRecovered = this.annotationStore.recoveredDocuments().has(docKey);
    const answerRecovered = this.answerStore.recovered().has(ptpKey);
    if (annoRecovered || answerRecovered) {
      this.toast.info(
        'Unsaved progress restored',
        'Your previous unsaved work has been restored. Save to keep it.',
      );
    }
    if (annoRecovered) this.annotationStore.acknowledgeRecovery(docKey);
    if (answerRecovered) this.answerStore.acknowledgeRecovery(ptpKey);
  }

  private openPaperDocument(docKey: string, kind: 'file' | 'url', ref: string): void {
    this.annotationStore.openDocument({
      source_kind: kind,
      source_ref: ref,
      content_hash: docKey,
      page_count: 0,
    });
  }

  private async loadClaimsForDocument(docKey: string): Promise<void> {
    try {
      const claims = await this.claimsProvider.loadFor(docKey);
      this.claims.setForDocument(docKey, claims);
    } catch {
      this.claims.setForDocument(docKey, []);
    }
  }

  onAnswerChange(event: { questionId: number; value: string }): void {
    const key = this.ptpKey;
    if (!key) return;
    this.answerStore.setAnswer(key, event.questionId, event.value);
  }

  /** Unified Save: use Promise.allSettled for independent save of both stores (A11). */
  async onSaveProgress(): Promise<void> {
    if (this.saving()) return;
    const paper = this.currentPaper();
    if (!paper) return;

    // A completed paper must stay valid (decision 8). Block an invalid save entirely so the
    // annotation changes (e.g. a removed evidence box) don't persist while the answer-save is
    // rejected, which would leave the completed paper corrupt. The edits stay on screen as unsaved.
    if (this.editingCompleted() && !this.canComplete()) {
      this.lastError.set(
        'This completed paper cannot be saved while incomplete: ' +
          this.blockedReason() +
          '. Re-add the missing answer or evidence box, or use "Back to extraction queue".',
      );
      return;
    }

    const docKey = `paper:${paper.paperId}`;
    const ptpKey = String(paper.paperToProjectId);

    this.saving.set(true);
    this.lastError.set(null);

    const [answerResult, annotationResult] = await Promise.allSettled([
      this.answerStore.submitChanges(ptpKey),
      this.annotationStore.submitChanges(docKey),
    ]);

    this.saving.set(false);

    const parts: string[] = [];
    if (answerResult.status === 'rejected') {
      // When editing an already-COMPLETED submission, a rejected answer-save means the backend
      // refused the invalid edit and rolled it back (the stored answers are unchanged). Keeping
      // the rejected local edit on screen (A11) would falsely look saved, so revert to the last
      // saved values and make it unmistakable the change was NOT saved.
      if (this.editingCompleted()) {
        this.answerStore.discardChanges(ptpKey);
        parts.push(
          'Answers were not saved (reverted to the last saved values): ' +
            this.asyncErrorMessage(answerResult.reason),
        );
      } else {
        parts.push('Answers: ' + this.asyncErrorMessage(answerResult.reason));
      }
    }
    if (annotationResult.status === 'rejected') {
      parts.push('Annotations: ' + this.asyncErrorMessage(annotationResult.reason));
    }

    // A save can race the PI completing the stage: the backend then rejects with the phase error.
    // Surface a single clear message (not a per-store error) so the reviewer knows the edit window
    // has closed and their unsaved changes are gone.
    const phaseRejected =
      (answerResult.status === 'rejected' && this.isPhaseError(answerResult.reason)) ||
      (annotationResult.status === 'rejected' && this.isPhaseError(annotationResult.reason));
    if (phaseRejected) {
      this.lastError.set(
        'This stage was completed by the PI; your unsaved changes can no longer be saved.',
      );
      return;
    }

    if (parts.length > 0) {
      this.lastError.set(parts.join(' | '));
    } else {
      this.toast.success('Progress saved', 'Your answers and annotations have been saved.');
      this.closeWorkspace();
    }
  }

  /**
   * True when an error is the backend "wrong phase" rejection. The backend returns HTTP 409 for
   * this, but 409 is also used for legitimate validation failures (e.g. a missing evidence box),
   * so the status alone is ambiguous. We additionally match the stable message marker the backend
   * emits ("Data Extraction phase") to tell the phase error apart.
   */
  private isPhaseError(e: unknown): boolean {
    if (!(e instanceof HttpErrorResponse) || e.status !== 409) return false;
    const body = e.error as Record<string, string> | null;
    const msg = body?.message ?? body?.detail ?? body?.error ?? '';
    return msg.includes('Data Extraction phase');
  }

  /** Complete: must save first, then call complete endpoint (A12). */
  async onComplete(): Promise<void> {
    if (!this.canComplete() || this.completing()) return;
    const paper = this.currentPaper();
    if (!paper) return;

    // Save first if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      await this.onSaveProgress();
      if (this.lastError()) return; // save failed, abort
    }

    this.completing.set(true);
    this.lastError.set(null);
    try {
      await firstValueFrom(
        this.dataExtractionService.complete(this.projectId(), paper.paperToProjectId),
      );
      this.toast.success('Paper completed', 'Your extraction has been recorded.');
      this.loadStats();
      this.loadCompleted();
      if (this.isPI()) this.loadConflictStatus(this.projectId());
      await this.loadNextPaper();
    } catch (e: unknown) {
      if (e instanceof HttpErrorResponse && e.status === 401) {
        void this.router.navigate(['/login']);
        return;
      }
      this.lastError.set(this.asyncErrorMessage(e));
    } finally {
      this.completing.set(false);
    }
  }

  async onSkip(): Promise<void> {
    if (this.skipping()) return;
    const paper = this.currentPaper();
    if (!paper) return;
    this.skipping.set(true);
    this.lastError.set(null);
    try {
      await firstValueFrom(
        this.dataExtractionService.skip(this.projectId(), paper.paperToProjectId),
      );
      this.loadStats();
      await this.loadNextPaper();
    } catch (e: unknown) {
      if (e instanceof HttpErrorResponse && e.status === 401) {
        void this.router.navigate(['/login']);
        return;
      }
      this.lastError.set(this.asyncErrorMessage(e));
    } finally {
      this.skipping.set(false);
    }
  }

  unlockCurrent(): void {
    const paper = this.currentPaper();
    if (!paper || this.loading()) return;
    this.discardCurrentPaperEdits();
    this.currentPaper.set(null);
    this.dataExtractionService.unlockPaper(this.projectId(), paper.paperToProjectId).subscribe({
      next: () => void this.router.navigate(['/projects', this.projectId()]),
      error: () => void this.router.navigate(['/projects', this.projectId()]),
    });
  }

  /** Open the near-fullscreen extraction workspace modal (questions + PDF editor). */
  openWorkspace(): void {
    this.workspaceOpen.set(true);
    // After a reload, resumeCurrentOrWait restores the current paper but not its PDF, claims, and
    // saved annotations; load them when the workspace opens (once per paper) so it is never empty.
    const paper = this.currentPaper();
    if (paper && this.loadedDocKey !== `paper:${paper.paperId}`) {
      const token = ++this.loadToken;
      this.loading.set(true);
      void this.loadPaperData(paper, token).finally(() => {
        if (token === this.loadToken) this.loading.set(false);
      });
    }
  }

  /** Close the extraction workspace modal. */
  closeWorkspace(): void {
    this.workspaceOpen.set(false);
  }

  /** Go back to the previous view. In COMPLETED projects, this restores search state and scroll. */
  onBackToQueue(): void {
    if (this.project()?.state === 'COMPLETED') {
      this.location.back();
    } else {
      void this.loadNextPaper();
    }
  }

  /** Map an error to a human-readable message (A9/A10). */
  asyncErrorMessage(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 401) {
        void this.router.navigate(['/login']);
        return 'Your session has expired. Please log in again.';
      }
      if (e.status === 0) {
        return 'Could not reach the server. Check your connection and try again.';
      }
      if (e.status >= 500) {
        return 'The server ran into a problem. Please try again in a moment.';
      }
      const body = e.error as Record<string, string> | null;
      const serverMsg = body?.message ?? body?.detail ?? body?.error;
      return serverMsg || 'The change could not be saved. Check your input and try again.';
    }
    return (e as Error)?.message || 'An unexpected error occurred.';
  }

  // PDF viewer event handlers (mirror annotator page)
  onPageRendered(ev: PageRenderedEvent): void {
    const vp = ev.viewport as {
      convertToPdfPoint(x: number, y: number): [number, number];
      convertToViewportPoint(x: number, y: number): [number, number];
      viewBox: [number, number, number, number];
    };
    const geom: PageGeometry = {
      pageWidthPdf: vp.viewBox[2] - vp.viewBox[0],
      pageHeightPdf: vp.viewBox[3] - vp.viewBox[1],
      toPdfPoint: (s) => {
        const [x, y] = vp.convertToPdfPoint(s.x, s.y);
        return { x, y };
      },
      toScreenPoint: (p) => {
        const [x, y] = vp.convertToViewportPoint(p.x, p.y);
        return { x, y };
      },
    };
    this.pageGeometries.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, geom);
      return next;
    });
    this.pageElements.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, ev.pageElement);
      return next;
    });
  }

  geometryFor(page: number): PageGeometry | null {
    return this.pageGeometries().get(page) ?? null;
  }
  pageElementFor(page: number): HTMLElement | null {
    return this.pageElements().get(page) ?? null;
  }
  pages(): number[] {
    return Array.from(this.pageGeometries().keys()).sort((a, b) => a - b);
  }
  activeDocumentId(): string {
    return this.annotationStore.activeDocumentId() ?? '';
  }
  currentClaims(): Claim[] {
    const id = this.annotationStore.activeDocumentId();
    return id ? this.claims.getForDocument(id) : [];
  }

  onOverlayDraw(ev: OverlayDrawCompleted): void {
    this.lastDrawClient.set({ x: ev.clientX, y: ev.clientY });
    this.workflow.proposeBox(ev.pageNumber, ev.rect);
  }
  onOverlayTextPoint(ev: OverlayTextPointPicked): void {
    this.workflow.proposeTextPoint({
      pageNumber: ev.pageNumber,
      pdfX: ev.pdfX,
      pdfY: ev.pdfY,
      clientX: ev.clientX,
      clientY: ev.clientY,
    });
  }
  onTextInputConfirmed(payload: { text: string; width: number; height: number }): void {
    const point = this.workflow.pendingTextPoint();
    if (!point) return;
    const geom = this.geometryFor(point.pageNumber);
    if (!geom) {
      this.workflow.cancelTextInput();
      return;
    }
    const p1 = geom.toPdfPoint({ x: 0, y: 0 });
    const p2 = geom.toPdfPoint({ x: payload.width, y: payload.height });
    const pdfWidth = Math.abs(p2.x - p1.x);
    const pdfHeight = Math.abs(p2.y - p1.y);
    this.workflow.provideTextContent({ text: payload.text, pdfWidth, pdfHeight });
  }
  onTextInputCancelled(): void {
    this.workflow.cancelTextInput();
  }
  onClaimPicked(claimId: string): void {
    this.workflow.commitWithClaim(claimId);
  }
  onClaimPickerCancelled(): void {
    this.workflow.cancelDraft();
  }
  onEditNoteRequested(event: { id: string; clientX: number; clientY: number }): void {
    // Anchor the note editor to the note button the user just clicked (near its box). The button
    // swallows pointerdown, so lastPointer would otherwise hold a stale, unrelated position.
    this.lastPointer.set({ x: event.clientX, y: event.clientY });
    this.workflow.editNote(event.id);
  }
  onDeleteAnnotation(annotationId: string): void {
    this.annotationStore.removeAnnotation(annotationId);
  }
  onNoteSaved(value: string): void {
    this.workflow.saveNote(value);
  }
  onNoteEditorCancelled(): void {
    this.workflow.cancelNoteEdit();
  }
  onOcrTextClick(event: { annotationId: string; page: number }): void {
    this.pdfViewer.scrollTo(event.page, 0);
    const seq = ++this.highlightSeq;
    this.highlightedAnnotation.set({ id: event.annotationId, seq });
    setTimeout(() => {
      if (this.highlightedAnnotation()?.id === event.annotationId) {
        this.highlightedAnnotation.set(null);
      }
    }, 3000);
  }
  onOcrTextSave(event: { annotationId: string; text: string }): void {
    this.annotationStore.setOcrText(event.annotationId, event.text === '' ? '' : event.text);
  }
  onEditOcrRequested(event: { id: string; clientX: number; clientY: number }): void {
    this.lastPointer.set({ x: event.clientX, y: event.clientY });
    this.workflow.editOcr(event.id);
  }
  onOcrSaved(value: string): void {
    this.workflow.saveOcr(value);
  }
  onOcrEditorCancelled(): void {
    this.workflow.cancelOcrEdit();
  }
  toggleBoxMode(): void {
    this.drawMode.update((m) => (m === 'box' ? null : 'box'));
  }
  toggleTextMode(): void {
    this.drawMode.update((m) => (m === 'text' ? null : 'text'));
  }
  onViewerLoadFailed(): void {
    this.lastError.set('The PDF failed to load in the viewer.');
  }

  /**
   * Anchor point for the note editor: the last pointerdown location (the note button the user just
   * clicked), falling back to the viewport center when no pointer has been recorded yet.
   */
  noteEditorAnchor(): { x: number; y: number } {
    const p = this.lastPointer();
    if (p) return p;
    const w = typeof window !== 'undefined' ? window.innerWidth : 0;
    const h = typeof window !== 'undefined' ? window.innerHeight : 0;
    return { x: w / 2, y: h / 2 };
  }

  /**
   * Clamp a horizontal client coordinate so the inner popover (claim picker / note / text input,
   * roughly 300px wide) stays fully on screen when anchored near the draw/click point.
   */
  clampX(x: number): number {
    const width = 300;
    const max = (typeof window !== 'undefined' ? window.innerWidth : width) - width - 8;
    return Math.max(8, Math.min(x, Math.max(8, max)));
  }

  /** Clamp a vertical client coordinate so the inner popover (roughly 240px tall) stays on screen. */
  clampY(y: number): number {
    const height = 240;
    const max = (typeof window !== 'undefined' ? window.innerHeight : height) - height - 8;
    return Math.max(8, Math.min(y, Math.max(8, max)));
  }

  get progressPercentage(): number {
    const s = this.stats();
    if (!s || s.totalPapers === 0) return 0;
    return (s.completedByAll / s.totalPapers) * 100;
  }
}

/** Check if a value is considered "answered" for a given question type. */
function isAnswered(q: ExtractionQuestion, value: string | undefined): boolean {
  if (!value || value.trim() === '') return false;
  if (q.type === 'MULTI_CHOICE') return value.trim() !== '[]';
  return true;
}
